import React, { FC, memo, useEffect, useState } from 'react';
import { FaFeatherAlt as SignatureIcon } from '@react-icons/all-files/fa/FaFeatherAlt';
import { createUseStyles } from 'src/theming';
import SignatureDialog from './components/SignatureDialog';
import { useFormContext } from 'react-hook-form';
import { FormHelperText, IconButton, Typography } from '@material-ui/core';
import { MdDelete as DeleteIcon } from '@react-icons/all-files/md/MdDelete';
import { MdRefresh as RetryIcon } from '@react-icons/all-files/md/MdRefresh';

const useStyles = createUseStyles({
  emptyContainer: {
    border: '2px dashed #dee2e6',
    padding: '30px 15px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    '& > img': {
      alignSelf: 'center',
      marginTop: 15,
      maxWidth: 150,
      maxHeight: 150,
    },
  },
  title: {
    marginTop: 5,
    fontSize: 15,
    fontWeight: 700,
    color: '#6c757d',
  },
  subtitle: {
    fontSize: 13,
    color: '#98a6ad',
  },
  section: {
    color: '#637381',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actions: {},
});

interface Props {
  required?: boolean;
  mainText?: any;
  footerText?: any;
}

const SignatureField: FC<Props> = React.forwardRef<any, Props>(({ mainText, footerText, required }, ref) => {
  const classes = useStyles();
  const [isSignatureDialogOpened, setIsSignatureDialogOpened] = useState(false);

  const {
    setValue,
    watch,
    register,
    formState: { errors },
  } = useFormContext<{ signature: string }>();

  useEffect(() => {
    register('signature', { required: required ? { value: true, message: 'Le signature est requise' } : false });
  }, []);

  const closeSignatureDialog = () => {
    setIsSignatureDialogOpened(false);
  };

  const handleRemoveSignature = () => {
    setValue('signature', '');
  };

  const handleValidateSignature = (signature: string) => {
    setValue('signature', signature, { shouldValidate: true });
    setIsSignatureDialogOpened(false);
  };

  const signature = watch('signature');

  return (
    <>
      {signature ? (
        <div className={classes.container}>
          <div className={classes.header}>
            <Typography variant='overline' className={classes.section}>
              Signature
            </Typography>

            <div className={classes.actions}>
              <IconButton onClick={() => setIsSignatureDialogOpened(true)}>
                <RetryIcon />
              </IconButton>

              <IconButton onClick={handleRemoveSignature}>
                <DeleteIcon />
              </IconButton>
            </div>
          </div>

          <img alt='signature' src={signature} />
        </div>
      ) : (
        <div className={classes.emptyContainer} onClick={() => setIsSignatureDialogOpened(true)}>
          <SignatureIcon color='#98a6ad' size={18} />
          <div className={classes.title}>Signature</div>
          <div className={classes.subtitle}>Cliquez pour signer</div>
        </div>
      )}

      {errors.signature && <FormHelperText error>{errors.signature.message}</FormHelperText>}

      <SignatureDialog
        mainText={mainText}
        footerText={footerText}
        open={isSignatureDialogOpened}
        onValidate={handleValidateSignature}
        onClose={closeSignatureDialog}
      />
    </>
  );
});

export default memo(SignatureField);
